import { NavLink } from "react-router-dom";
import { useState } from "react";
import type { CSSProperties } from "react";
import { useProjects, useAllItems, useFolders, useFolderMutations } from "@/hooks/useData";
import { pct } from "@/lib/format";
import { Dot } from "./atoms";
import { usePinnedProjects } from "@/lib/theme";
import { projectTree, type ProjectNode } from "@/lib/deps";
import type { Project } from "@/lib/types";

const eyebrow: CSSProperties = {
  fontFamily: '"DM Mono", monospace',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.045em",
  textTransform: "uppercase",
  color: "var(--muted2)",
  padding: "0 8px 8px",
};

function navStyle(active: boolean): CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: 11,
    padding: "9px 13px",
    borderRadius: 999,
    fontSize: 13.5,
    cursor: "pointer",
    border: "none",
    textAlign: "left",
    width: "100%",
    background: active ? "var(--accentTint)" : "transparent",
    color: active ? "var(--accent)" : "var(--ink2)",
    fontWeight: active ? "var(--fw-title)" : "var(--fw-medium)",
    textDecoration: "none",
  };
}


// The PROJECTS heading is a nav row that looks like a section label (§2.3).
const projectsHeading = (active: boolean): CSSProperties => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "7px 11px",
  borderRadius: 999,
  textDecoration: "none",
  fontFamily: '"DM Mono", monospace',
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.045em",
  textTransform: "uppercase",
  background: active ? "var(--accentTint)" : "transparent",
  color: active ? "var(--accent)" : "var(--muted2)",
});

// One level of nesting: folder > project > sub-project.
const indent: CSSProperties = { marginLeft: 14, paddingLeft: 8, borderLeft: "1px solid var(--line)" };

function itemProjectId(item: any): string | undefined {
  return item?.expand?.phase?.expand?.project?.id ?? item?.expand?.phase?.project;
}

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { data: projects = [] } = useProjects();
  const { data: items = [] } = useAllItems();
  const { data: folders = [] } = useFolders();
  const folderMut = useFolderMutations();
  /** Swap a folder with its neighbour; the order is saved, so it holds everywhere. */
  function moveFolder(i: number, d: number) {
    const j = i + d;
    if (j < 0 || j >= folders.length) return;
    const next = [...folders];
    [next[i], next[j]] = [next[j], next[i]];
    folderMut.reorder.mutate(next);
  }
  // Collapse state is per-folder and client-side only (§2.4). Absent = open.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [pins] = usePinnedProjects();



  // Build the parent/child tree first, then file its ROOTS into folders. A
  // sub-project follows its parent and is never filed separately — grouping the
  // flat list instead would strand any child whose parent carries the folder.
  const visible = projects.filter((p) => p.kind !== "inbox" && p.status !== "archived");
  const tree = projectTree(visible);
  const pinnedProjects = visible.filter((p) => pins.includes(p.id));
  const folderIds = new Set(folders.map((f) => f.id));
  const inFolder = (f: { id: string }) => tree.filter((n) => n.project.folderId === f.id);
  const unfiled = tree.filter((n) => !n.project.folderId || !folderIds.has(n.project.folderId));

  return (
    <aside
      style={{
        // --sidebar-w (index.css): narrower on a small window, and the top
        // bar's brand follows it so the folder tabs still start at the page.
        width: "var(--sidebar-w)",
        flex: "none",
        height: "100%",
        // No borderRight: the content pane's own top+left border and rounded
        // top-left corner are the separation. A second line here just doubled it.
        background: "var(--bg2)",
        display: "flex",
        flexDirection: "column",
      }}
      className="no-print"
    >
      {/* Projects: the heading is the link to the Projects page (§2.3) */}
      {/* The main pages and the account row moved to the top bar (TopNav), so
          the sidebar is projects, top to bottom. */}
      <div style={{ flex: "1 1 auto", minHeight: 96, overflowY: "auto", padding: "8px 10px 14px" }}>
        <NavLink to="/projects" onClick={onNavigate} style={({ isActive }) => projectsHeading(isActive)}>
          <span>Projects</span>
          <span aria-hidden="true" style={{ fontSize: 13, lineHeight: 1 }}>›</span>
        </NavLink>

        <div style={{ display: "flex", flexDirection: "column", gap: 1, marginTop: 4 }}>
          {/* Pinned projects come first, flat — the point of pinning is to skip
              the tree, so they are not nested under their folder here. */}
          {pinnedProjects.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div style={{ paddingLeft: 11, fontSize: 10, fontWeight: "var(--fw-title)", letterSpacing: "var(--ls-eyebrow)", textTransform: "uppercase", color: "var(--muted3)", marginBottom: 2 }}>
                Pinned
              </div>
              {pinnedProjects.map((p) => (
                <ProjectLink key={p.id} project={p} items={items} depth={0} onNavigate={onNavigate} />
              ))}
            </div>
          )}

          {folders.map((f, fi) => {
            const members = inFolder(f);
            const open = !collapsed[f.id];
            return (
              <div key={f.id}>
                {/* The arrows sit beside the toggle, not inside it: a button
                    can't hold buttons. They show on hover (always on touch),
                    as the board cards' reorder arrows do. */}
                <div className="folder-row" style={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <button
                    onClick={() => setCollapsed((c) => ({ ...c, [f.id]: open }))}
                    aria-expanded={open}
                    style={{
                      display: "flex", alignItems: "center", gap: 8, flex: 1, minWidth: 0,
                      padding: "7px 11px", borderRadius: 999, border: "none", background: "transparent",
                      color: "var(--ink2)", fontSize: 13.5, fontWeight: "var(--fw-medium)",
                      cursor: "pointer", textAlign: "left",
                    }}
                  >
                    <span aria-hidden="true" style={{ width: 10, flex: "none", color: "var(--muted2)", fontSize: 10 }}>{open ? "▾" : "▸"}</span>
                    <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                    <span className="mono" style={{ fontSize: 10.5, color: "var(--muted2)" }}>{members.length}</span>
                  </button>
                  {folders.length > 1 && (
                    <span className="folder-row-actions" style={{ display: "flex", flex: "none" }}>
                      <button onClick={() => moveFolder(fi, -1)} disabled={fi === 0} aria-label={`Move ${f.name} up`} title="Move up" style={folderArrow(fi === 0)}>↑</button>
                      <button onClick={() => moveFolder(fi, 1)} disabled={fi === folders.length - 1} aria-label={`Move ${f.name} down`} title="Move down" style={folderArrow(fi === folders.length - 1)}>↓</button>
                    </span>
                  )}
                </div>
                {open && members.length > 0 && (
                  <div style={indent}>
                    {members.map((node) => (
                      <ProjectBranch key={node.project.id} node={node} items={items} onNavigate={onNavigate} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {unfiled.map((node) => (
            <ProjectBranch key={node.project.id} node={node} items={items} onNavigate={onNavigate} />
          ))}
        </div>
      </div>

    </aside>
  );
}


function ProjectLink({ project, items, depth, onNavigate }: {
  project: Project; items: any[]; depth: number; onNavigate?: () => void;
}) {
  const root = depth === 0;
  return (
    <NavLink
      to={`/project/${project.id}`}
      onClick={onNavigate}
      style={({ isActive }) => ({
        ...navStyle(isActive),
        justifyContent: "space-between",
        padding: root ? "7px 11px" : "6px 10px",
        fontSize: root ? 13.5 : 13,
      })}
    >
      <span style={{ display: "flex", alignItems: "center", gap: root ? 9 : 8, minWidth: 0 }}>
        <Dot color={project.color} size={root ? 7 : 6} />
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{project.name}</span>
      </span>
    </NavLink>
  );
}

/** A project and its sub-projects, each level indented one further. */
function ProjectBranch({ node, items, onNavigate, depth = 0 }: {
  node: ProjectNode; items: any[]; onNavigate?: () => void; depth?: number;
}) {
  return (
    <div>
      <ProjectLink project={node.project} items={items} depth={depth} onNavigate={onNavigate} />
      {node.children.length > 0 && (
        <div style={indent}>
          {node.children.map((c) => (
            <ProjectBranch key={c.project.id} node={c} items={items} depth={depth + 1} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
}

function projectPct(projectId: string, items: any[]): number {
  const mine = (items || []).filter((i) => itemProjectId(i) === projectId);
  const done = mine.filter((i) => i.status === "done").length;
  return pct(done, mine.length);
}

const folderArrow = (disabled: boolean): CSSProperties => ({
  width: 20, height: 22, padding: 0, border: "none", borderRadius: 6, background: "transparent",
  color: disabled ? "var(--muted3)" : "var(--muted)", fontSize: 11, lineHeight: 1,
  cursor: disabled ? "default" : "pointer", opacity: disabled ? 0.4 : 1,
});
